import {Controller, useFormContext} from "react-hook-form";
import {DatePicker, DatePickerProps, Space} from "antd";
import {FormField, FormFieldError} from "../../../../../../../design-system/form-field";
import {CreateKycBasicFormStepFormValues} from "../../schema-validation";
import moment, {Moment} from "moment";

export /** Parse every backend variant we’ve seen → Moment | undefined */
function parseFromBackend(raw: string | null | undefined): Moment | undefined {
  if (!raw) return undefined;

  const trimmedRaw = raw.trim();

  // Explicit sentinel the backend sometimes sends
  if (trimmedRaw.toLowerCase() === "invalid date") return undefined;

  // a) Long JavaScript Date.toString() form
  const withoutLabel = trimmedRaw.replace(/\s*\(.*\)$/, "");
  const longForm = moment.parseZone(
    withoutLabel,
    "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
    true // strict
  );
  if (longForm.isValid()) return longForm;

  // b) ISO YYYY-MM-DD form (backend v2 format)
  const iso = moment(trimmedRaw, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso;

  // c) Simple DD-MM-YYYY form
  const dmy = moment(trimmedRaw, "DD-MM-YYYY", true);
  if (dmy.isValid()) return dmy;

  return undefined; // anything else is junk
}

/** Serialise Moment (or null) to the *only* format the backend accepts */
export function serialiseToBackend(m: Moment | null): string | null {
  return m ? m.format("DD-MM-YYYY") : null;
}

export function KycDateOfBirthFormField() {
  const {control} = useFormContext<CreateKycBasicFormStepFormValues>();

  const disabledDate: DatePickerProps["disabledDate"] = (current) => {
    // Disable dates after today minus 18 years
    return current && current > moment().subtract(18, "years").endOf("day");
  };

  return (
    <FormField
      id="dob"
      label="Date of birth"
      renderInput={(id) => (
        <Controller
          control={control}
          name="dateOfBirth"
          render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => {
            const pickerMoment = parseFromBackend(value);
            return (
              <Space size={8} direction="vertical" style={{display: "flex"}}>
                <DatePicker
                  id={id}
                  status={error?.message ? "error" : ""}
                  onChange={(m) => onChange(serialiseToBackend(m ?? null))}
                  onBlur={onBlur}
                  size="large"
                  value={pickerMoment ?? undefined}
                  placeholder=""
                  ref={ref}
                  disabledDate={disabledDate}
                />
                <FormFieldError errorMessage={error?.message} />
              </Space>
            );
          }}
        />
      )}
    />
  );
}
